import {
  Msg,
  type AnyMessage,
  type FireMsg,
  type HelloMsg,
  type HitConfirmMsg,
  type DeathMsg,
  type InputMsg,
  type MatchMsg,
  type DamagedMsg,
  type ShotMsg,
  type ScoreMsg,
  type RosterEntry,
  type RosterMsg,
  type MatchSummaryMsg,
  type MatchSummaryRow,
  type SnapshotEntity,
  type SnapshotMsg,
  type SnapshotProjectile,
  type ThrowMsg,
  type ExplosionMsg,
  type WelcomeMsg,
} from './messages.js';

// Byte layouts (little-endian). Angles are f32 for Phase 0 simplicity;
// quantisation to i16 is a later bandwidth optimisation.
const SNAP_HEADER = 1 + 4 + 4 + 2 + 1; // type, serverTick, ackSeq, entityCount, projCount
// id(2) team(1) flags(1) x,y,z,yaw,vx,vy,vz(28) health(1) pitch(4) weaponId(1) suppression(1) ammo(1) blind(1)
const SNAP_ENTITY = 2 + 1 + 1 + 4 * 7 + 1 + 4 + 1 + 1 + 1 + 1; // = 41
const SNAP_PROJ = 2 + 1 + 1 + 4 * 3; // id, kind, gtype, x,y,z = 16

function viewOf(data: ArrayBuffer | ArrayBufferView): DataView {
  if (data instanceof ArrayBuffer) return new DataView(data);
  return new DataView(data.buffer, data.byteOffset, data.byteLength);
}

const textEnc = new TextEncoder();
const textDec = new TextDecoder();

/** Write a u8-length-prefixed UTF-8 string at `o`; returns bytes written. */
function putStr(dv: DataView, o: number, s: string): number {
  const bytes = textEnc.encode(s).slice(0, 255);
  dv.setUint8(o, bytes.length);
  new Uint8Array(dv.buffer, dv.byteOffset + o + 1, bytes.length).set(bytes);
  return 1 + bytes.length;
}

/** Read a u8-length-prefixed UTF-8 string at `o`. */
function getStr(dv: DataView, o: number): { s: string; size: number } | null {
  if (o >= dv.byteLength) return null;
  const len = dv.getUint8(o);
  if (o + 1 + len > dv.byteLength) return null;
  const s = textDec.decode(new Uint8Array(dv.buffer, dv.byteOffset + o + 1, len));
  return { s, size: 1 + len };
}

function strSize(s: string): number {
  return 1 + Math.min(255, textEnc.encode(s).length);
}

export function encodeHello(m: Omit<HelloMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(2 + strSize(m.name));
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Hello);
  dv.setUint8(1, m.preferredTeam & 0xff);
  putStr(dv, 2, m.name);
  return buf;
}

export function encodeRoster(m: Omit<RosterMsg, 'type'>): ArrayBuffer {
  let size = 2; // type + count
  for (const p of m.players) size += 8 + strSize(p.name);
  const buf = new ArrayBuffer(size);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Roster);
  dv.setUint8(1, m.players.length & 0xff);
  let o = 2;
  for (const p of m.players) {
    dv.setUint16(o, p.id & 0xffff, true);
    dv.setUint8(o + 2, p.teamId & 0xff);
    dv.setUint16(o + 3, p.kills & 0xffff, true);
    dv.setUint16(o + 5, p.deaths & 0xffff, true);
    dv.setUint8(o + 7, p.flags & 0xff);
    o += 8;
    o += putStr(dv, o, p.name);
  }
  return buf;
}

// MatchSummary fixed header (after the type byte): winnerTeam(1) score0(2)
// score1(2) durationMs(4) totalShots(2) totalHits(2) totalKills(2)
// totalDeaths(2) totalAssists(2) longestStreak(1) = 20 bytes.
const SUMMARY_HEAD = 1 + 2 + 2 + 4 + 2 + 2 + 2 + 2 + 2 + 1;
const SUMMARY_ROW = 1 + 2 + 2 + 2 + 2 + 1 + 1; // teamId score kills deaths assists weaponId flags

export function encodeMatchSummary(m: Omit<MatchSummaryMsg, 'type'>): ArrayBuffer {
  let size = 1 + SUMMARY_HEAD; // type + fixed header
  size += strSize(m.matchId) + strSize(m.longestStreakName);
  size += 1; // row count
  for (const r of m.rows) size += SUMMARY_ROW + strSize(r.name);
  const buf = new ArrayBuffer(size);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.MatchSummary);
  dv.setUint8(1, m.winnerTeam & 0xff);
  dv.setInt16(2, m.score0, true);
  dv.setInt16(4, m.score1, true);
  dv.setUint32(6, m.durationMs >>> 0, true);
  dv.setUint16(10, m.totalShots & 0xffff, true);
  dv.setUint16(12, m.totalHits & 0xffff, true);
  dv.setUint16(14, m.totalKills & 0xffff, true);
  dv.setUint16(16, m.totalDeaths & 0xffff, true);
  dv.setUint16(18, m.totalAssists & 0xffff, true);
  dv.setUint8(20, m.longestStreak & 0xff);
  let o = 1 + SUMMARY_HEAD;
  o += putStr(dv, o, m.matchId);
  o += putStr(dv, o, m.longestStreakName);
  dv.setUint8(o, m.rows.length & 0xff);
  o += 1;
  for (const r of m.rows) {
    dv.setUint8(o, r.teamId & 0xff);
    dv.setUint16(o + 1, r.score & 0xffff, true);
    dv.setUint16(o + 3, r.kills & 0xffff, true);
    dv.setUint16(o + 5, r.deaths & 0xffff, true);
    dv.setUint16(o + 7, r.assists & 0xffff, true);
    dv.setUint8(o + 9, r.weaponId & 0xff);
    dv.setUint8(o + 10, r.flags & 0xff);
    o += SUMMARY_ROW;
    o += putStr(dv, o, r.name);
  }
  return buf;
}

export function encodeInput(m: Omit<InputMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(16);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Input);
  dv.setUint32(1, m.seq >>> 0, true);
  dv.setUint16(5, m.buttons & 0xffff, true);
  dv.setFloat32(7, m.yaw, true);
  dv.setFloat32(11, m.pitch, true);
  dv.setUint8(15, m.weaponId & 0xff);
  return buf;
}

export function encodeFire(m: Omit<FireMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(22);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Fire);
  dv.setUint32(1, m.seq >>> 0, true);
  dv.setUint32(5, m.renderTick >>> 0, true);
  dv.setFloat32(9, m.dx, true);
  dv.setFloat32(13, m.dy, true);
  dv.setFloat32(17, m.dz, true);
  dv.setUint8(21, m.weaponId & 0xff);
  return buf;
}

export function encodeHitConfirm(m: Omit<HitConfirmMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(5);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.HitConfirm);
  dv.setUint16(1, m.victimId & 0xffff, true);
  dv.setUint8(3, m.dmg & 0xff);
  dv.setUint8(4, m.lethal ? 1 : 0);
  return buf;
}

export function encodeDeath(m: Omit<DeathMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(7);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Death);
  dv.setUint16(1, m.victimId & 0xffff, true);
  dv.setUint16(3, m.killerId & 0xffff, true);
  dv.setUint8(5, m.weaponId & 0xff);
  dv.setUint8(6, (m.headshot ? 1 : 0) | (m.friendlyFire ? 2 : 0));
  return buf;
}

export function encodeScore(m: Omit<ScoreMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(5);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Score);
  dv.setInt16(1, m.team0, true);
  dv.setInt16(3, m.team1, true);
  return buf;
}

export function encodeMatch(m: Omit<MatchMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(16);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Match);
  dv.setUint8(1, m.state & 0xff);
  dv.setUint32(2, m.msLeft >>> 0, true);
  dv.setUint8(6, m.killTarget & 0xff);
  dv.setUint8(7, m.winnerTeam & 0xff);
  dv.setFloat32(8, m.windDir, true);
  dv.setFloat32(12, m.windSpeed, true);
  return buf;
}

export function encodeDamaged(m: Omit<DamagedMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(6);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Damaged);
  dv.setUint8(1, m.dmg & 0xff);
  dv.setFloat32(2, m.dirYaw, true);
  return buf;
}

export function encodeShot(m: Omit<ShotMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(28);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Shot);
  dv.setUint16(1, m.shooterId & 0xffff, true);
  dv.setUint8(3, m.weaponId & 0xff);
  dv.setFloat32(4, m.x, true);
  dv.setFloat32(8, m.y, true);
  dv.setFloat32(12, m.z, true);
  dv.setFloat32(16, m.dx, true);
  dv.setFloat32(20, m.dy, true);
  dv.setFloat32(24, m.dz, true);
  return buf;
}

export function encodeWelcome(m: Omit<WelcomeMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(6);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Welcome);
  dv.setUint16(1, m.entityId & 0xffff, true);
  dv.setUint8(3, m.teamId & 0xff);
  dv.setUint8(4, m.tickHz & 0xff);
  dv.setUint8(5, m.snapHz & 0xff);
  return buf;
}

export function encodeSnapshot(m: Omit<SnapshotMsg, 'type'>): ArrayBuffer {
  const n = m.entities.length;
  const np = m.projectiles.length;
  const buf = new ArrayBuffer(SNAP_HEADER + n * SNAP_ENTITY + np * SNAP_PROJ);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Snapshot);
  dv.setUint32(1, m.serverTick >>> 0, true);
  dv.setUint32(5, m.ackSeq >>> 0, true);
  dv.setUint16(9, n, true);
  dv.setUint8(11, np & 0xff);
  let o = SNAP_HEADER;
  for (const e of m.entities) {
    dv.setUint16(o, e.id & 0xffff, true);
    dv.setUint8(o + 2, e.teamId & 0xff);
    dv.setUint8(o + 3, e.flags & 0xff);
    dv.setFloat32(o + 4, e.x, true);
    dv.setFloat32(o + 8, e.y, true);
    dv.setFloat32(o + 12, e.z, true);
    dv.setFloat32(o + 16, e.yaw, true);
    dv.setFloat32(o + 20, e.vx, true);
    dv.setFloat32(o + 24, e.vy, true);
    dv.setFloat32(o + 28, e.vz, true);
    dv.setUint8(o + 32, Math.max(0, Math.min(255, Math.round(e.health))));
    dv.setFloat32(o + 33, e.pitch, true);
    dv.setUint8(o + 37, e.weaponId & 0xff);
    dv.setUint8(o + 38, Math.max(0, Math.min(255, Math.round(e.suppression))));
    dv.setUint8(o + 39, Math.max(0, Math.min(255, Math.round(e.ammo))));
    dv.setUint8(o + 40, Math.max(0, Math.min(255, Math.round(e.blind))));
    o += SNAP_ENTITY;
  }
  for (const p of m.projectiles) {
    dv.setUint16(o, p.id & 0xffff, true);
    dv.setUint8(o + 2, p.kind & 0xff);
    dv.setUint8(o + 3, p.gtype & 0xff);
    dv.setFloat32(o + 4, p.x, true);
    dv.setFloat32(o + 8, p.y, true);
    dv.setFloat32(o + 12, p.z, true);
    o += SNAP_PROJ;
  }
  return buf;
}

export function encodeThrow(m: Omit<ThrowMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(18);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Throw);
  dv.setUint32(1, m.seq >>> 0, true);
  dv.setFloat32(5, m.dx, true);
  dv.setFloat32(9, m.dy, true);
  dv.setFloat32(13, m.dz, true);
  dv.setUint8(17, m.gtype & 0xff);
  return buf;
}

export function encodeExplosion(m: Omit<ExplosionMsg, 'type'>): ArrayBuffer {
  const buf = new ArrayBuffer(15);
  const dv = new DataView(buf);
  dv.setUint8(0, Msg.Explosion);
  dv.setUint8(1, m.kind & 0xff);
  dv.setUint8(2, m.gtype & 0xff);
  dv.setFloat32(3, m.x, true);
  dv.setFloat32(7, m.y, true);
  dv.setFloat32(11, m.z, true);
  return buf;
}

/** Decode any framed message. Returns null on an unknown/short/garbled buffer. */
export function decode(data: ArrayBuffer | ArrayBufferView): AnyMessage | null {
  const dv = viewOf(data);
  if (dv.byteLength < 1) return null;
  const type = dv.getUint8(0);

  switch (type) {
    case Msg.Hello: {
      if (dv.byteLength < 2) return null;
      const name = dv.byteLength > 2 ? getStr(dv, 2) : { s: '', size: 0 };
      if (!name) return null;
      return { type: Msg.Hello, preferredTeam: dv.getUint8(1), name: name.s };
    }

    case Msg.Roster: {
      if (dv.byteLength < 2) return null;
      const count = dv.getUint8(1);
      const players: RosterEntry[] = [];
      let o = 2;
      for (let i = 0; i < count; i++) {
        if (o + 8 > dv.byteLength) return null;
        const id = dv.getUint16(o, true);
        const teamId = dv.getUint8(o + 2);
        const kills = dv.getUint16(o + 3, true);
        const deaths = dv.getUint16(o + 5, true);
        const flags = dv.getUint8(o + 7);
        o += 8;
        const name = getStr(dv, o);
        if (!name) return null;
        o += name.size;
        players.push({ id, teamId, kills, deaths, flags, name: name.s });
      }
      return { type: Msg.Roster, players };
    }

    case Msg.MatchSummary: {
      if (dv.byteLength < 1 + SUMMARY_HEAD) return null;
      const winnerTeam = dv.getUint8(1);
      const score0 = dv.getInt16(2, true);
      const score1 = dv.getInt16(4, true);
      const durationMs = dv.getUint32(6, true);
      const totalShots = dv.getUint16(10, true);
      const totalHits = dv.getUint16(12, true);
      const totalKills = dv.getUint16(14, true);
      const totalDeaths = dv.getUint16(16, true);
      const totalAssists = dv.getUint16(18, true);
      const longestStreak = dv.getUint8(20);
      let o = 1 + SUMMARY_HEAD;
      const matchId = getStr(dv, o);
      if (!matchId) return null;
      o += matchId.size;
      const streakName = getStr(dv, o);
      if (!streakName) return null;
      o += streakName.size;
      if (o >= dv.byteLength) return null;
      const count = dv.getUint8(o);
      o += 1;
      const rows: MatchSummaryRow[] = [];
      for (let i = 0; i < count; i++) {
        if (o + SUMMARY_ROW > dv.byteLength) return null;
        const teamId = dv.getUint8(o);
        const score = dv.getUint16(o + 1, true);
        const kills = dv.getUint16(o + 3, true);
        const deaths = dv.getUint16(o + 5, true);
        const assists = dv.getUint16(o + 7, true);
        const weaponId = dv.getUint8(o + 9);
        const flags = dv.getUint8(o + 10);
        o += SUMMARY_ROW;
        const name = getStr(dv, o);
        if (!name) return null;
        o += name.size;
        rows.push({ teamId, score, kills, deaths, assists, weaponId, flags, name: name.s });
      }
      return {
        type: Msg.MatchSummary,
        winnerTeam,
        score0,
        score1,
        matchId: matchId.s,
        durationMs,
        totalShots,
        totalHits,
        totalKills,
        totalDeaths,
        totalAssists,
        longestStreak,
        longestStreakName: streakName.s,
        rows,
      };
    }

    case Msg.Input:
      if (dv.byteLength < 16) return null;
      return {
        type: Msg.Input,
        seq: dv.getUint32(1, true),
        buttons: dv.getUint16(5, true),
        yaw: dv.getFloat32(7, true),
        pitch: dv.getFloat32(11, true),
        weaponId: dv.getUint8(15),
      };

    case Msg.Fire:
      if (dv.byteLength < 22) return null;
      return {
        type: Msg.Fire,
        seq: dv.getUint32(1, true),
        renderTick: dv.getUint32(5, true),
        dx: dv.getFloat32(9, true),
        dy: dv.getFloat32(13, true),
        dz: dv.getFloat32(17, true),
        weaponId: dv.getUint8(21),
      };

    case Msg.HitConfirm:
      if (dv.byteLength < 5) return null;
      return {
        type: Msg.HitConfirm,
        victimId: dv.getUint16(1, true),
        dmg: dv.getUint8(3),
        lethal: dv.getUint8(4) !== 0,
      };

    case Msg.Death: {
      if (dv.byteLength < 7) return null;
      const f = dv.getUint8(6);
      return {
        type: Msg.Death,
        victimId: dv.getUint16(1, true),
        killerId: dv.getUint16(3, true),
        weaponId: dv.getUint8(5),
        headshot: (f & 1) !== 0,
        friendlyFire: (f & 2) !== 0,
      };
    }

    case Msg.Score:
      if (dv.byteLength < 5) return null;
      return {
        type: Msg.Score,
        team0: dv.getInt16(1, true),
        team1: dv.getInt16(3, true),
      };

    case Msg.Match:
      if (dv.byteLength < 16) return null;
      return {
        type: Msg.Match,
        state: dv.getUint8(1),
        msLeft: dv.getUint32(2, true),
        killTarget: dv.getUint8(6),
        winnerTeam: dv.getUint8(7),
        windDir: dv.getFloat32(8, true),
        windSpeed: dv.getFloat32(12, true),
      };

    case Msg.Damaged:
      if (dv.byteLength < 6) return null;
      return {
        type: Msg.Damaged,
        dmg: dv.getUint8(1),
        dirYaw: dv.getFloat32(2, true),
      };

    case Msg.Throw:
      if (dv.byteLength < 18) return null;
      return {
        type: Msg.Throw,
        seq: dv.getUint32(1, true),
        dx: dv.getFloat32(5, true),
        dy: dv.getFloat32(9, true),
        dz: dv.getFloat32(13, true),
        gtype: dv.getUint8(17),
      };

    case Msg.Explosion:
      if (dv.byteLength < 15) return null;
      return {
        type: Msg.Explosion,
        kind: dv.getUint8(1),
        gtype: dv.getUint8(2),
        x: dv.getFloat32(3, true),
        y: dv.getFloat32(7, true),
        z: dv.getFloat32(11, true),
      };

    case Msg.Shot:
      if (dv.byteLength < 28) return null;
      return {
        type: Msg.Shot,
        shooterId: dv.getUint16(1, true),
        weaponId: dv.getUint8(3),
        x: dv.getFloat32(4, true),
        y: dv.getFloat32(8, true),
        z: dv.getFloat32(12, true),
        dx: dv.getFloat32(16, true),
        dy: dv.getFloat32(20, true),
        dz: dv.getFloat32(24, true),
      };

    case Msg.Welcome:
      if (dv.byteLength < 6) return null;
      return {
        type: Msg.Welcome,
        entityId: dv.getUint16(1, true),
        teamId: dv.getUint8(3),
        tickHz: dv.getUint8(4),
        snapHz: dv.getUint8(5),
      };

    case Msg.Snapshot: {
      if (dv.byteLength < SNAP_HEADER) return null;
      const serverTick = dv.getUint32(1, true);
      const ackSeq = dv.getUint32(5, true);
      const count = dv.getUint16(9, true);
      const projCount = dv.getUint8(11);
      if (dv.byteLength < SNAP_HEADER + count * SNAP_ENTITY + projCount * SNAP_PROJ) return null;
      const entities: SnapshotEntity[] = [];
      let o = SNAP_HEADER;
      for (let i = 0; i < count; i++) {
        entities.push({
          id: dv.getUint16(o, true),
          teamId: dv.getUint8(o + 2),
          flags: dv.getUint8(o + 3),
          x: dv.getFloat32(o + 4, true),
          y: dv.getFloat32(o + 8, true),
          z: dv.getFloat32(o + 12, true),
          yaw: dv.getFloat32(o + 16, true),
          vx: dv.getFloat32(o + 20, true),
          vy: dv.getFloat32(o + 24, true),
          vz: dv.getFloat32(o + 28, true),
          health: dv.getUint8(o + 32),
          pitch: dv.getFloat32(o + 33, true),
          weaponId: dv.getUint8(o + 37),
          suppression: dv.getUint8(o + 38),
          ammo: dv.getUint8(o + 39),
          blind: dv.getUint8(o + 40),
        });
        o += SNAP_ENTITY;
      }
      const projectiles: SnapshotProjectile[] = [];
      for (let i = 0; i < projCount; i++) {
        projectiles.push({
          id: dv.getUint16(o, true),
          kind: dv.getUint8(o + 2),
          gtype: dv.getUint8(o + 3),
          x: dv.getFloat32(o + 4, true),
          y: dv.getFloat32(o + 8, true),
          z: dv.getFloat32(o + 12, true),
        });
        o += SNAP_PROJ;
      }
      return { type: Msg.Snapshot, serverTick, ackSeq, entities, projectiles };
    }

    default:
      return null;
  }
}
